import React, { useCallback } from "react";
import { ActivityIndicator, FlatList, Text, View } from "react-native";
import { useCrew } from "@/context/CrewProvider";
import { trpc } from "@/trpc/react";
import { ConversationListItem } from "@/components/messages/conversation-list-item";
import { useFocusEffect } from "@react-navigation/native";

// Define a conversation type that matches exactly what the component expects
type Conversation = {
    id: number;
    name: string;
    type: string;
    jobId: number | null;
    lastMessage: string;
    lastMessageSender: string;
    lastMessageTimestamp: Date;
    hasUnread: boolean;
    participants: Array<{
        id: number;
        isAdmin: boolean;
        staffId: number | null;
        crewId: number | null;
        staffName: string | null;
        crewName: string | null;
        avatar: string | null;
    }>;
};

export default function MessagesScreen() {
    const { currentCrewMember } = useCrew();

    const {
        data: conversations,
        isLoading,
        isError,
        refetch
    } = trpc.messages.getConversationsForCrew.useQuery(
        { crew_id: currentCrewMember?.id! },
        {
            enabled: !!currentCrewMember?.id,
            // Poll every 15 seconds to keep conversations fresh
            refetchInterval: 15000,
        }
    );

    // Refresh conversations list when screen is focused (single immediate refetch)
    useFocusEffect(
        useCallback(() => {
            if (currentCrewMember?.id) {
                refetch();
            }
        }, [currentCrewMember?.id, refetch])
    );

    if (isLoading) {
        return (
            <View className="flex-1 justify-center items-center">
                <ActivityIndicator size="large" color="#0000ff" />
            </View>
        );
    }

    if (isError) {
        return (
            <View className="flex-1 justify-center items-center p-4">
                <Text className="text-red-500 text-center mb-4">
                    Failed to load conversations
                </Text>
                <Text className="text-center text-gray-500">
                    Pull down to try again
                </Text>
            </View>
        );
    }

    if (!conversations || conversations.length === 0) {
        return (
            <View className="flex-1 justify-center items-center p-4">
                <Text className="text-xl font-bold text-center mb-2">No messages yet</Text>
                <Text className="text-center text-gray-500">
                    When someone messages you, you'll see their conversations here.
                </Text>
            </View>
        );
    }

    // Transform conversations to ensure correct types
    const safeConversations: Conversation[] = conversations.map(conv => ({
        ...conv,
        participants: conv.participants.map(p => ({
            ...p,
            staffName: p.staffName || null, // Convert undefined to null
            avatar: p.avatar || null       // Ensure avatar is never undefined
        }))
    }));

    return (
        <FlatList
            data={safeConversations}
            keyExtractor={(item) => item.id.toString()}
            renderItem={({ item }) => (
                <ConversationListItem conversation={item} />
            )}
            onRefresh={refetch}
            refreshing={isLoading}
            className="bg-white flex-1"
        />
    );
} 